import React from 'react';
import { Icon } from 'rsuite';
import { BsChatFill, BsFillChatFill } from 'react-icons/bs';
import { FaImage, FaMicrophone, FaVideo } from 'react-icons/fa';

/* Global Constants */

export const ROOT_URL =
  process.env.NODE_ENV === 'production'
    ? ('https://belouga.org' as const)
    : ('https://staging.belouga.org' as const);

export const API_URL = `${ROOT_URL}/v2api`;

export const USER_ROLES = {
  ADMIN: 1,
  TEACHER: 2,
  STUDENT: 3,
} as const;

export const USER_AGE = {
  MINOR: 1,
  ADULT: 2,
} as const;

export const USER_ACCESS_LEVELS = {
  FREE: 1,
  PAID: 2,
} as const;

export const EPISODE_SECTION_TYPES = {
  VIDEO: 1,
  ACTIVITY: 2,
} as const;

export const REACTION_TYPE_IDS = {
  LIKE: '1',
  CELEBRATE: '2',
  SUPPORT: '3',
  INSIGHTFUL: '4',
  CURIOUS: '5',
} as const;

export const EMOJI_PICKER_TYPES = {
  PT_USER_COMMENT: 1,
  PT_COMMENT_REACTION: 2,
} as const;

export const SONAR_GROUP_TYPES = {
  INDIVIDUAL: 1,
  CLASSROOM: 2,
};
export const SONAR_GROUP_MEMBER_STATUS = {
  PENDING: 1,
  ACCEPTED: 2,
  REJECTED: 3,
  MUTED: 4,
};

export type EmojiProperties = {
  unicode: string;
  code: string;
  label: string;
};

export const EMOJIS: {
  [index: string]: EmojiProperties;
} = {
  smile: { unicode: '\u{1f600}', code: ':smile:', label: 'Smiley Face' },
  think: { unicode: '\u{1f914}', code: ':think:', label: 'Thinking Face' },
  thumbsUp: { unicode: '\u{1f44d}', code: ':thumbsUp:', label: 'Thumbs Up' },
  globe: { unicode: '\u{1f30d}', code: ':globe:', label: 'Globe' },
  wavingHand: {
    unicode: '\u{1f44b}',
    code: ':wavingHand:',
    label: 'Waving Hand',
  },
  raisedHands: {
    unicode: '\u{1f64c}',
    code: ':raisedHands:',
    label: 'Raised Hands',
  },
  clapping: {
    unicode: '\u{1f44f}',
    code: ':celebrate:',
    label: 'Clapping Hands',
  },

  redHeart: {
    unicode: '\u2764\ufe0f',
    code: ':redHeart:',
    label: 'Red Heart',
  },
  lightbulb: {
    unicode: '\u{1f4a1}',
    code: ':lightbulb:',
    label: 'Lightbulb',
  },
} as const;

interface ReactionTypeProperties {
  color?: string;
  emoji: EmojiProperties;
  iconSolid?: string;
  iconOutline?: string;
}

export const MEDIA_TYPE_IDS = {
  IMAGE: 1,
  VIDEO: 2,
} as const;

export const PROMPT_TYPE_IDS = {
  INFO: 1,
  QUESTION: 2,
  TEXT: 3,
} as const;

export const RESPONSE_TYPE_IDS = {
  AUDIO: 1,
  VIDEO: 2,
  IMAGE: 3,
  TEXT: 4,
  DRAW: 5,
} as const;

export const FLAGGED_CONTENT_TYPES = {
  SONAR_MESSAGE: 1,
  PROMPT_COMMENT: 2,
  RESPONSE_COMMENT: 3,
} as const;

export const FLAG_REASONS = {
  FC_LANGUAGE: 'Inappropriate Language',
  FC_BEHAVIOR: 'Inappropriate Behavior',
  FC_AGE: 'Age Inappropriate/Other',
  FC_MEDIA: 'Inappropriate Image/Video',
  FC_BULLYING: 'Bullying',
} as const;

export const RESPONSE_TYPE_ICONS: Record<number, React.ReactElement> = {
  [RESPONSE_TYPE_IDS.AUDIO]: <FaMicrophone />,
  [RESPONSE_TYPE_IDS.IMAGE]: <FaImage />,
  [RESPONSE_TYPE_IDS.VIDEO]: <FaVideo />,
  [RESPONSE_TYPE_IDS.DRAW]: <Icon icon="paint-brush" />,
  [RESPONSE_TYPE_IDS.TEXT]: <BsFillChatFill />,
} as const;

export const REACTION_TYPES: {
  [index: string]: ReactionTypeProperties;
} = {
  [`${REACTION_TYPE_IDS.LIKE}`]: {
    color: 'primary',
    emoji: EMOJIS.redHeart,
  },
  [`${REACTION_TYPE_IDS.CELEBRATE}`]: {
    color: 'primary',
    emoji: EMOJIS.clapping,
  },
  [`${REACTION_TYPE_IDS.SUPPORT}`]: {
    color: 'primary',
    emoji: EMOJIS.thumbsUp,
  },
  [`${REACTION_TYPE_IDS.INSIGHTFUL}`]: {
    color: 'success',
    emoji: EMOJIS.lightbulb,
  },
  [`${REACTION_TYPE_IDS.CURIOUS}`]: { color: 'primary', emoji: EMOJIS.think },
} as const;

export const DEFAULT_AVATAR = '/assets/demo/av5.png';

export const momentJsLocale = {
  future: 'in %s',
  past: '%s ago',
  s: 'just now',
  ss: '%ds',
  m: '1min',
  mm: '%dmin',
  h: '1h',
  hh: '%dh',
  d: '1d',
  dd: '%dd',
  w: '1w',
  ww: '%dw',
  M: '1m',
  MM: '%dm',
  y: '1y',
  yy: '%dy',
};
